/**
 * Layout for spelling sheets: the numbered test, the look–say–cover–write–check practice
 * grid and the teacher's copy. Pure functions from a word list and a page to positions in
 * millimetres.
 */

import { chunkBy, type PageGeometry } from "@/lib/tool-kit/printables/page";
import { createRng, shuffled } from "@/lib/tool-kit/worksheets/random";

export interface SpellingEntry {
  word: string;
  /** An optional dictation sentence for the teacher's copy. */
  sentence: string;
}

export const MAX_WORDS = 100;
export const MAX_WORD_LENGTH = 60;

const COMMA = /\s*,\s*/;
const SENTENCE_SPLIT = /\s*[|;]\s*/;

/**
 * Words from pasted text. One per line, `word | sentence` for a dictation sentence; a
 * single line with commas is read as a comma-separated list.
 */
export function parseSpellingList(
  raw: string,
  limit: number = MAX_WORDS
): SpellingEntry[] {
  const lines = raw
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
  const singleLine = lines.length === 1 && !(lines[0] ?? "").includes("|");
  const rows = singleLine ? (lines[0] ?? "").split(COMMA) : lines;
  const entries: SpellingEntry[] = [];
  for (const row of rows) {
    const [word = "", ...rest] = row.split(SENTENCE_SPLIT);
    const trimmed = word.trim().slice(0, MAX_WORD_LENGTH);
    if (trimmed.length > 0) {
      entries.push({ word: trimmed, sentence: rest.join(" ").trim() });
    }
  }
  return entries.slice(0, limit);
}

/** The list in a seeded random order (the same seed always gives the same order). */
export function shuffleEntries(
  entries: readonly SpellingEntry[],
  seed: number
): SpellingEntry[] {
  return shuffled(createRng(seed), entries);
}

/** Space above the first row for the title and the Name / Date / Score line. */
export function spellingHeaderHeight(
  title: boolean,
  nameLine: boolean
): number {
  const block = (title ? 11 : 0) + (nameLine ? 10 : 0);
  return block > 0 ? block + 3 : 0;
}

/** Rows that fit in `available` mm, at least one. */
export function rowsThatFit(available: number, rowHeight: number): number {
  return Math.max(1, Math.floor(available / rowHeight));
}

/* ----------------------------------------------------------------------- test */

export interface TestCell {
  number: number;
  x: number;
  y: number;
  width: number;
}

export interface TestSentence {
  number: number;
  y: number;
}

export interface TestPage {
  first: boolean;
  cells: TestCell[];
  sentences: TestSentence[];
}

export interface TestOptions {
  columns: 1 | 2;
  rowHeight: number;
  /** Full-width dictation lines after the words. */
  sentenceLines: number;
  headerHeight: number;
}

const COLUMN_GAP = 12;

/** The numbered test: words flow down each column, then across, then onto a new page. */
export function layoutTest(
  count: number,
  options: TestOptions,
  geometry: PageGeometry
): TestPage[] {
  const width =
    (geometry.contentWidth - COLUMN_GAP * (options.columns - 1)) /
    options.columns;
  const numbers = Array.from({ length: count }, (_, i) => i + 1);
  const pages: TestPage[] = [];
  let remaining = numbers;
  let first = true;
  while (remaining.length > 0 || first) {
    const header = first ? options.headerHeight : 0;
    const perColumn = rowsThatFit(
      geometry.contentHeight - header,
      options.rowHeight
    );
    const take = remaining.slice(0, perColumn * options.columns);
    remaining = remaining.slice(take.length);
    const cells: TestCell[] = [];
    chunkBy(take, perColumn).forEach((column, columnIndex) => {
      column.forEach((number, rowIndex) => {
        cells.push({
          number,
          x: geometry.left + columnIndex * (width + COLUMN_GAP),
          y: geometry.top + header + rowIndex * options.rowHeight,
          width,
        });
      });
    });
    pages.push({ first, cells, sentences: [] });
    first = false;
    if (pages.length >= 20) {
      break;
    }
  }
  addSentences(pages, count, options, geometry);
  return pages;
}

/** Puts the dictation lines on the last page, or on a new one if they do not fit. */
function addSentences(
  pages: TestPage[],
  count: number,
  options: TestOptions,
  geometry: PageGeometry
): void {
  if (options.sentenceLines <= 0) {
    return;
  }
  const last = pages.at(-1);
  if (!last) {
    return;
  }
  const sentenceHeight = options.rowHeight * 1.4;
  const bottom = geometry.top + geometry.contentHeight;
  let y = geometry.top + (last.first ? options.headerHeight : 0);
  for (const cell of last.cells) {
    y = Math.max(y, cell.y + options.rowHeight);
  }
  // Columns share rows, so the used height is the tallest column.
  let target = last;
  if (y + sentenceHeight * options.sentenceLines > bottom + 0.01) {
    target = { first: false, cells: [], sentences: [] };
    pages.push(target);
    y = geometry.top;
  }
  for (let i = 0; i < options.sentenceLines; i += 1) {
    target.sentences.push({ number: count + i + 1, y: y + i * sentenceHeight });
  }
}

/* ------------------------------------------------------------------- practice */

export interface PracticeRow {
  number: number;
  entryIndex: number;
  y: number;
}

export interface PracticePage {
  first: boolean;
  /** y of the step-label row. */
  headerY: number;
  rows: PracticeRow[];
}

export interface PracticeOptions {
  writeColumns: number;
  rowHeight: number;
  headerHeight: number;
}

export const PRACTICE_LABEL_ROW = 9;

export interface PracticeColumns {
  /** The printed word. */
  word: { x: number; width: number };
  /** One entry per write-it box. */
  write: { x: number; width: number }[];
  /** The tick box. */
  check: { x: number; width: number };
  right: number;
}

/** Column x positions: a word column, the write boxes, and a narrow check box. */
export function practiceColumns(
  geometry: PageGeometry,
  writeColumns: number
): PracticeColumns {
  const total = geometry.contentWidth;
  const checkWidth = 22;
  const wordWidth = total * (writeColumns === 1 ? 0.3 : 0.24);
  const writeWidth = (total - wordWidth - checkWidth) / writeColumns;
  const left = geometry.left;
  const write = Array.from({ length: writeColumns }, (_, i) => ({
    x: left + wordWidth + i * writeWidth,
    width: writeWidth,
  }));
  return {
    word: { x: left, width: wordWidth },
    write,
    check: {
      x: left + wordWidth + writeWidth * writeColumns,
      width: checkWidth,
    },
    right: left + total,
  };
}

/** The practice grid, one row per word, with the step labels repeated on each page. */
export function layoutPractice(
  count: number,
  options: PracticeOptions,
  geometry: PageGeometry
): PracticePage[] {
  const pages: PracticePage[] = [];
  let next = 0;
  let first = true;
  while (next < count || first) {
    const header = first ? options.headerHeight : 0;
    const perPage = rowsThatFit(
      geometry.contentHeight - header - PRACTICE_LABEL_ROW,
      options.rowHeight
    );
    const headerY = geometry.top + header;
    const rows: PracticeRow[] = [];
    for (let i = 0; i < perPage && next < count; i += 1) {
      rows.push({
        number: next + 1,
        entryIndex: next,
        y: headerY + PRACTICE_LABEL_ROW + i * options.rowHeight,
      });
      next += 1;
    }
    pages.push({ first, headerY, rows });
    first = false;
    if (pages.length >= 20) {
      break;
    }
  }
  return pages;
}

/* ------------------------------------------------------------------------ key */

export interface KeyRow {
  number: number;
  entryIndex: number;
  x: number;
  y: number;
  width: number;
}

export interface KeyPage {
  first: boolean;
  rows: KeyRow[];
}

/** The teacher's copy: one column when there are sentences, two otherwise. */
export function layoutKey(
  entries: readonly SpellingEntry[],
  rowHeight: number,
  headerHeight: number,
  geometry: PageGeometry
): KeyPage[] {
  const columns = entries.some((entry) => entry.sentence) ? 1 : 2;
  const width = (geometry.contentWidth - COLUMN_GAP * (columns - 1)) / columns;
  const pages: KeyPage[] = [];
  let next = 0;
  let first = true;
  while (next < entries.length || first) {
    const header = first ? headerHeight : 0;
    const perColumn = rowsThatFit(geometry.contentHeight - header, rowHeight);
    const take = Math.min(entries.length - next, perColumn * columns);
    const rows: KeyRow[] = [];
    for (let i = 0; i < take; i += 1) {
      const column = Math.floor(i / perColumn);
      rows.push({
        number: next + i + 1,
        entryIndex: next + i,
        x: geometry.left + column * (width + COLUMN_GAP),
        y: geometry.top + header + (i % perColumn) * rowHeight,
        width,
      });
    }
    pages.push({ first, rows });
    next += take;
    first = false;
    if (pages.length >= 20) {
      break;
    }
  }
  return pages;
}
